/**
 * Kopierar kundlogotyper och kontaktfoton till Firebase Storage när de
 * hämtats från LinkedIn eller bolagsdata, så att de inte försvinner om den
 * externa adressen slutar fungera. Nedskalad till högst 256 px, JPEG.
 *
 * Anropas efter att kunden eller kontakten sparats (dokumentet måste ha ett
 * id). Körs i bakgrunden: ett fel loggas men stoppar aldrig sparandet.
 * Befintliga bilder kopieras av scripts/backfill-bildkopior.js.
 */
import { doc, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import {
  BILDFALT, BILDKOPIA_MAX_PX, behoverKopia, kundLoggaPath, kontaktFotoPath,
} from './bilder';

/**
 * Bild (Blob) → JPEG-Blob med största sidan högst maxPx. Vit bakgrund, så
 * att genomskinliga loggor inte blir svarta. Exporteras för exportens
 * nedskalning (utils/excelBilder.js).
 */
export async function skalaBild(blob, maxPx, kvalitet = 0.85) {
  const bild = await createImageBitmap(blob);
  const skala = Math.min(1, maxPx / Math.max(bild.width, bild.height));
  const w = Math.max(1, Math.round(bild.width * skala));
  const h = Math.max(1, Math.round(bild.height * skala));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bild, 0, 0, w, h);
  bild.close?.();
  return new Promise((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Kunde inte koda bilden'))), 'image/jpeg', kvalitet);
  });
}

async function kopiera(dok, typ, path, dokRef) {
  if (!behoverKopia(dok, typ)) return null;
  const f = BILDFALT[typ];
  const url = dok[f.url].trim();
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const jpeg = await skalaBild(await res.blob(), BILDKOPIA_MAX_PX);
    await uploadBytes(storageRef(storage, path), jpeg, {
      contentType: 'image/jpeg', cacheControl: 'public, max-age=31536000',
    });
    const falt = { [f.path]: path, [f.kalla]: url };
    await updateDoc(dokRef, falt);
    return falt;
  } catch (e) {
    console.warn(`Kunde inte spara kopia av ${typ === 'logo' ? 'logotypen' : 'fotot'} (${url})`, e);
    return null;
  }
}

/** Kundens logga till kunder/{id}/logo.jpg. dok = kundens fält efter sparandet. */
export function sparaKundlogga(kundId, dok) {
  if (!kundId) return Promise.resolve(null);
  return kopiera(dok, 'logo', kundLoggaPath(kundId), doc(db, 'crmCompanies', kundId));
}

/** Kontaktens foto till kontakter/{id}/foto.jpg. dok = kontaktens fält efter sparandet. */
export function sparaKontaktfoto(kundId, kontaktId, dok) {
  if (!kundId || !kontaktId) return Promise.resolve(null);
  return kopiera(dok, 'foto', kontaktFotoPath(kontaktId), doc(db, 'crmCompanies', kundId, 'contacts', kontaktId));
}
